import { forwardRef, lazy, useImperativeHandle, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Copy, ExternalLink, Info } from 'lucide-react';
import { Alert as InfoAlert } from 'reactstrap';
import { addDays, format, parseISO } from 'date-fns';

import Edit from 'src/componentes/edit/edit-dialog';
import { useSnackbar } from 'notistack';
import http from 'src/services/httpService';
import {
  STATUS_PESQUISA_CLIMA,
  TIPO_IDENTIFICACAO_FORMULARIO_GLOBAL,
  TIPO_ORIGEM,
} from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import Alert from 'src/componentes/message-box/alert';
import CriticalConfirm from 'src/componentes/message-box/critical-confirm';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import Tab from 'src/componentes/tabs/tab';
import Tabs from 'src/componentes/tabs/tabs';
import Input from 'src/componentes/input';
import useAuth from 'src/hooks/useAuth';
import errorHandler from 'src/utils/error-handler';
import PlanoAcao from 'src/paginas/NewGantt';
import {
  copyToClipboard,
  FORMATO_EXIBICAO_FORMULARIO_GLOBAL,
  getDataAplicacaoFormularioGlobal,
  getLookupId,
  getPesquisaClimaLink,
  TIPO_APLICACAO_FORMULARIO_GLOBAL,
  TIPO_TERMINO_RECORRENCIA,
} from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';
import DadosPesquisaClimaTab from './components/DadosPesquisaClimaTab';
import DashboardPesquisaClimaTab from './components/DashboardPesquisaClimaTab';
import EnvioPesquisaClimaTab from './components/EnvioPesquisaClimaTab';
import FormularioPesquisaClimaTab from './components/FormularioPesquisaClimaTab';
import ParticipantesPesquisaClimaTab from './components/ParticipantesPesquisaClimaTab';
import RespostasPesquisaClimaTab from './components/RespostasPesquisaClimaTab';

const ConfirmarInicioPesquisaModal = lazy(() =>
  import('./components/ConfirmarInicioPesquisaModal')
);

const isEmpty = (value) =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

const normalizeFormatoExibicao = (value) =>
  getLookupId(value) === FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PERGUNTA_POR_VEZ
    ? FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PERGUNTA_POR_VEZ
    : FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PAGINA_UNICA;

const normalizeDataRecorrencia = (value) => {
  if (!value) return null;

  return value instanceof Date
    ? format(value, 'yyyy-MM-dd')
    : String(value).slice(0, 10);
};

const getDiaCalendario = (value) => {
  const data = getDataAplicacaoFormularioGlobal(value);

  return data && !Number.isNaN(data.getTime())
    ? format(data, 'yyyy-MM-dd')
    : null;
};

const getConfiguracaoRecorrencia = (model) => {
  const aplicacao = model?.aplicacao || {};
  const tipoAplicacaoId =
    getLookupId(aplicacao.tipoAplicacaoId) ||
    TIPO_APLICACAO_FORMULARIO_GLOBAL.UNICA;

  if (tipoAplicacaoId !== TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE) {
    return { tipoAplicacaoId };
  }

  const tipoTerminoRecorrenciaId = getLookupId(
    aplicacao.tipoTerminoRecorrenciaId
  );

  return {
    tipoAplicacaoId,
    dataInicio: normalizeDataRecorrencia(model.dataInicio),
    frequenciaId: getLookupId(aplicacao.frequencia),
    periodoDisponivelRespostaDias: Number(
      aplicacao.periodoDisponivelRespostaDias
    ),
    tipoTerminoRecorrenciaId,
    quantidadeAplicacoes:
      tipoTerminoRecorrenciaId ===
      TIPO_TERMINO_RECORRENCIA.QUANTIDADE_APLICACOES
        ? Number(aplicacao.quantidadeAplicacoes)
        : null,
    dataTermino:
      tipoTerminoRecorrenciaId === TIPO_TERMINO_RECORRENCIA.DATA_ESPECIFICA
        ? normalizeDataRecorrencia(aplicacao.dataTermino)
        : null,
  };
};

const recorrenciaAlterada = (model, modelInicial) =>
  getLookupId(modelInicial?.aplicacao?.tipoAplicacaoId) ===
    TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE &&
  JSON.stringify(getConfiguracaoRecorrencia(model)) !==
    JSON.stringify(getConfiguracaoRecorrencia(modelInicial));

const getDataTerminoAplicacao = (model) => {
  if (
    getLookupId(model.aplicacao?.tipoAplicacaoId) !==
    TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE
  ) {
    return model.dataTermino;
  }

  const dataInicio =
    model.dataInicio instanceof Date
      ? model.dataInicio
      : parseISO(String(model.dataInicio).slice(0, 10));

  return addDays(
    dataInicio,
    Number(model.aplicacao?.periodoDisponivelRespostaDias) - 1
  );
};

const createPesquisaClimaValidation = (intl) => {
  const fieldSchemas = {
    idIntegracao: {
      _label: intl.formatMessage({ id: 'idIntegracao' }),
    },
    nome: { _label: intl.formatMessage({ id: 'nome' }) },
    area: { _label: intl.formatMessage({ id: 'area' }) },
    responsaveis: { _label: intl.formatMessage({ id: 'responsaveis' }) },
    descricao: { _label: intl.formatMessage({ id: 'descricao' }) },
    tipoIdentificacao: {
      _label: intl.formatMessage({ id: 'labelTipoPesquisa' }),
    },
  };

  return {
    getSchema: (path) => fieldSchemas[path],
    validate: (model = {}) => {
      const errors = [];

      if (isEmpty(model.nome)) {
        errors.push({
          path: 'nome',
          message: intl.formatMessage({ id: 'campoObrigatorioNaoInformado' }),
        });
      }

      if (isEmpty(model.responsaveis)) {
        errors.push({
          path: 'responsaveis',
          message: intl.formatMessage({ id: 'campoObrigatorioNaoInformado' }),
        });
      }

      if (isEmpty(model.tipoIdentificacao)) {
        errors.push({
          path: 'tipoIdentificacao',
          message: intl.formatMessage({ id: 'campoObrigatorioNaoInformado' }),
        });
      }

      return errors;
    },
  };
};

const INFORMACOES_TAB_INDEX = 0;
const FORMULARIO_TAB_INDEX = 1;
const PARTICIPANTES_TAB_INDEX = 2;
const ENVIO_TAB_INDEX = 3;
const RESPOSTAS_TAB_INDEX = 4;
const DASHBOARD_TAB_INDEX = 5;

function FormEditContent(
  {
    activeTabIndex,
    aplicacaoRefreshToken,
    index,
    linkPesquisa,
    model,
    onAplicacaoAlterada,
    onTabChange,
    showEnvioRequiredErrors,
  },
  ref
) {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const formularioTabRef = useRef();
  const participantesTabRef = useRef();
  const entidadeId = model.getModel('id').value;
  const isNew = isEmpty(entidadeId) || Number(entidadeId) === 0;
  const status = model.getModel('status').value;
  const statusId = getLookupId(status);
  const pesquisaEnviada =
    !isNew && !!statusId && statusId !== STATUS_PESQUISA_CLIMA.RASCUNHO;
  const participantesDisabled = [
    STATUS_PESQUISA_CLIMA.ENCERRADA,
    STATUS_PESQUISA_CLIMA.CANCELADA,
  ].includes(statusId);
  const formularioId = model.getModel('formularioId').value;
  const camposDisabled = model.getModel('podeAlterar').value === false;
  const datasDisabled = model.getModel('podeAlterarDatas').value === false;
  const mostrarBannerIniciada = camposDisabled && !datasDisabled;
  const tipoIdentificacao = model.getModel('tipoIdentificacao').value;
  const tipoIdentificacaoId = Number(
    tipoIdentificacao?.id ?? tipoIdentificacao
  );
  const ehConfidencial = [
    TIPO_IDENTIFICACAO_FORMULARIO_GLOBAL.CONFIDENCIAL,
    TIPO_IDENTIFICACAO_FORMULARIO_GLOBAL.ANONIMA,
  ].includes(tipoIdentificacaoId);

  const filtroDrawerFocusTrapPriority = Number(index || 0) + 2;

  const linkAcessoPesquisa =
    linkPesquisa ||
    getPesquisaClimaLink({
      linkPesquisa: model.getModel('linkPesquisa').value,
      token: model.getModel('token').value,
    });

  useImperativeHandle(ref, () => ({
    validateFormularioGlobal: () =>
      formularioTabRef.current?.validateFormularioGlobal(),
    saveFormularioGlobal: (savedEntidadeId) =>
      formularioTabRef.current?.saveFormularioGlobal(savedEntidadeId),
    saveParticipantes: (savedEntidadeId) =>
      participantesTabRef.current?.saveParticipantes(savedEntidadeId),
    getResumoIniciarPesquisa: async () => {
      let participantesCount = participantesTabRef.current
        ? participantesTabRef.current.getParticipantesCount()
        : null;
      let perguntasCount = formularioTabRef.current
        ? formularioTabRef.current.getPerguntasCount()
        : null;

      // Caso não tenha alterado a aba deve buscar o resumo salvo
      if (participantesCount == null || perguntasCount == null) {
        try {
          const response = await http.get('/PesquisaClima/ObterResumoInicio', {
            params: { id: entidadeId },
          });

          if (participantesCount == null) {
            participantesCount = response.data?.quantidadeParticipantes;
          }
          if (perguntasCount == null) {
            perguntasCount = response.data?.quantidadePerguntas;
          }
        } catch (err) {
          errorHandler(err);
        }
      }

      return {
        participantesCount: participantesCount ?? 0,
        perguntasCount: perguntasCount ?? 0,
      };
    },
  }));

  return (
    <>
      {mostrarBannerIniciada && (
        <InfoAlert color="info" className="d-flex align-items-start">
          <Info size={18} className="mr-2 mt-1 flex-shrink-0" />
          <span>
            {intl.formatMessage({ id: 'labelPesquisaClimaIniciadaInfoEdicao' })}
          </span>
        </InfoAlert>
      )}
      <Tabs currenttabIndex={activeTabIndex} onSelect={onTabChange}>
        <Tab
          label={capitalizeFirstLetter(
            intl.formatMessage({ id: 'label.informacoes' })
          )}
        >
          <DadosPesquisaClimaTab disabled={camposDisabled} model={model} />
        </Tab>
        {!isNew && (
          <Tab
            label={capitalizeFirstLetter(
              intl.formatMessage({ id: 'formulario' })
            )}
          >
            <FormularioPesquisaClimaTab
              active={activeTabIndex === FORMULARIO_TAB_INDEX}
              disabled={camposDisabled}
              entidadeId={entidadeId}
              formularioId={formularioId}
              randomizarPerguntas={
                !!model.getModel('randomizarPerguntas').value
              }
              ref={formularioTabRef}
            />
          </Tab>
        )}
        {!isNew && (
          <Tab
            label={capitalizeFirstLetter(
              intl.formatMessage({ id: 'participantes' })
            )}
          >
            <ParticipantesPesquisaClimaTab
              active={activeTabIndex === PARTICIPANTES_TAB_INDEX}
              disabled={participantesDisabled}
              entidadeId={entidadeId}
              filtroDrawerFocusTrapPriority={filtroDrawerFocusTrapPriority}
              ref={participantesTabRef}
            />
          </Tab>
        )}
        {!isNew && (
          <Tab
            label={capitalizeFirstLetter(
              intl.formatMessage({ id: 'labelEnvioPesquisa' })
            )}
          >
            <EnvioPesquisaClimaTab
              disabled={camposDisabled}
              disabledDatas={datasDisabled}
              linkPesquisa={linkAcessoPesquisa}
              model={model}
              previewFocusTrapPriority={filtroDrawerFocusTrapPriority}
              showRequiredErrors={showEnvioRequiredErrors}
            />
          </Tab>
        )}
        {pesquisaEnviada && (
          <Tab
            label={capitalizeFirstLetter(
              intl.formatMessage({ id: 'labelRespostas' })
            )}
          >
            <RespostasPesquisaClimaTab
              active={activeTabIndex === RESPOSTAS_TAB_INDEX}
              aplicacaoEmAndamento={
                statusId === STATUS_PESQUISA_CLIMA.EM_ANDAMENTO
              }
              aplicacaoRefreshToken={aplicacaoRefreshToken}
              entidadeId={entidadeId}
              ehConfidencial={ehConfidencial}
              filtroDrawerFocusTrapPriority={filtroDrawerFocusTrapPriority}
              onAplicacaoAlterada={onAplicacaoAlterada}
              podeEncerrar={model.getModel('podeEncerrar').value}
              podeIniciarProximaAplicacao={
                model.getModel('podeIniciarProximaAplicacao').value
              }
              periodoDisponivelRespostaDias={
                model
                  .getModel('aplicacao')
                  .getModel('periodoDisponivelRespostaDias').value
              }
            />
          </Tab>
        )}
        {pesquisaEnviada && (
          <Tab
            label={capitalizeFirstLetter(
              intl.formatMessage({ id: 'labelDashboard' })
            )}
          >
            <DashboardPesquisaClimaTab
              active={activeTabIndex === DASHBOARD_TAB_INDEX}
              aplicacaoRefreshToken={aplicacaoRefreshToken}
              entidadeId={entidadeId}
              formularioId={formularioId}
              recorrente={
                getLookupId(
                  model.getModel('aplicacao').getModel('tipoAplicacaoId').value
                ) === TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE
              }
              statusId={statusId}
            />
          </Tab>
        )}
        {!isNew && (
          <Tab label={capitalizeFirstLetter(resources.planodeacao)}>
            <PlanoAcao
              parameters={{
                entidadeId,
                tipoOrigemId: TIPO_ORIGEM.PESQUISA_CLIMA,
                exibirLookups: false,
                itemNaoObrigatorio: true,
              }}
              exibirLookups={false}
              showMenuToolBar={false}
              forceRefreshAfterChange
              ignorarFiltroSalvo
              desabilitarSincronizacao
            />
          </Tab>
        )}
      </Tabs>
    </>
  );
}

const FormEdit = forwardRef(FormEditContent);

function PesquisaClimaEditView({ ...rest }) {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const formComponentRef = useRef();
  const iniciarPesquisaAfterSaveRef = useRef(false);
  const confirmarAlteracaoRecorrenciaRef = useRef(false);
  const verificandoAplicacoesFuturasRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showEnvioRequiredErrors, setShowEnvioRequiredErrors] = useState(false);
  const [linkPesquisa, setLinkPesquisa] = useState('');
  const [aplicacaoRefreshToken, setAplicacaoRefreshToken] = useState(0);

  const isEnvioTab = activeTabIndex === ENVIO_TAB_INDEX;

  const selectInvalidPesquisaTab = (model) => {
    const [firstError] = createPesquisaClimaValidation(intl).validate(model);
    if (!firstError) return;

    setActiveTabIndex(
      firstError.path === 'tipoIdentificacao' && model.id > 0
        ? ENVIO_TAB_INDEX
        : INFORMACOES_TAB_INDEX
    );
  };

  const validateDataInicio = (model, modelInicial) => {
    const dataInicio = getDiaCalendario(model.dataInicio);
    const hoje = format(new Date(), 'yyyy-MM-dd');

    if (!dataInicio || dataInicio >= hoje) return true;

    const pesquisaJaIniciada =
      modelInicial?.status &&
      getLookupId(modelInicial.status) !== STATUS_PESQUISA_CLIMA.RASCUNHO;

    if (
      pesquisaJaIniciada &&
      getDiaCalendario(modelInicial.dataInicio) === dataInicio
    ) {
      return true;
    }

    ContentManager.addContent(
      <Alert
        message={intl.formatMessage({
          id: 'msgDataInicioNaoPodeSerAnteriorHoje',
        })}
      />
    );

    return false;
  };

  const validateEnvio = (model) => {
    const aplicacao = model.aplicacao || {};
    const tipoAplicacaoId =
      getLookupId(aplicacao.tipoAplicacaoId) ||
      TIPO_APLICACAO_FORMULARIO_GLOBAL.UNICA;
    const recorrente =
      tipoAplicacaoId === TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE;
    const tipoTerminoRecorrenciaId = getLookupId(
      aplicacao.tipoTerminoRecorrenciaId
    );
    let isValid;

    if (!recorrente) {
      isValid = !isEmpty(model.dataInicio) && !isEmpty(model.dataTermino);
    } else {
      isValid =
        !isEmpty(model.dataInicio) &&
        !!getLookupId(aplicacao.frequencia) &&
        Number(aplicacao.periodoDisponivelRespostaDias) >= 1 &&
        !!tipoTerminoRecorrenciaId;

      if (
        tipoTerminoRecorrenciaId ===
        TIPO_TERMINO_RECORRENCIA.QUANTIDADE_APLICACOES
      ) {
        isValid = isValid && Number(aplicacao.quantidadeAplicacoes) >= 1;
      }

      if (
        tipoTerminoRecorrenciaId === TIPO_TERMINO_RECORRENCIA.DATA_ESPECIFICA
      ) {
        isValid = isValid && !isEmpty(aplicacao.dataTermino);
      }
    }

    setShowEnvioRequiredErrors(!isValid);

    if (!isValid) {
      setActiveTabIndex(ENVIO_TAB_INDEX);
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
          })}
        />
      );
    }

    return isValid;
  };

  const showPesquisaIniciada = (link) => {
    setLinkPesquisa(link);

    ContentManager.addContent(
      <Alert
        title={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'labelPesquisaClimaIniciada' },
            { pesquisa: resources.pesquisa }
          )
        )}
        textCapitalizeTitle={false}
        titleCapitalizeNone={true}
        message={
          <div>
            <div className="d-flex align-items-center">
              <Input
                className="form-control"
                readOnly
                style={{ minWidth: 420, height: '34px' }}
                model={{
                  label: intl.formatMessage(
                    { id: 'labelLinkAcessoPesquisaClimaGerado' },
                    { pesquisa: resources.pesquisa }
                  ),
                  value: link,
                }}
              />
              <div className="pt-3 pb-1">
                <Button
                  className="ml-2"
                  color="outline-primary"
                  leftIcon={Copy}
                  style={{ minWidth: 150 }}
                  onClick={async () => {
                    await copyToClipboard(link);
                    enqueueSnackbar(
                      intl.formatMessage({ id: 'labelLinkCopiado' }),
                      { variant: 'success' }
                    );
                  }}
                >
                  {intl.formatMessage({ id: 'labelCopiarLink' })}
                </Button>
                <Button
                  className="ml-2"
                  style={{ minWidth: 150 }}
                  color="outline-primary"
                  leftIcon={ExternalLink}
                  onClick={() => window.open(link, '_blank')}
                >
                  {intl.formatMessage({ id: 'labelAbrirLink' })}
                </Button>
              </div>
            </div>
          </div>
        }
      />
    );
  };

  const iniciarPesquisa = async (pesquisaClimaId) => {
    const response = await http.post('/PesquisaClima/Iniciar', {
      id: pesquisaClimaId,
      parameters: rest.parameters,
    });
    const link = getPesquisaClimaLink(response.data);

    enqueueSnackbar(
      intl.formatMessage({ id: 'labelPesquisaIniciadaComSucesso' }),
      { variant: 'success' }
    );

    if (link) {
      showPesquisaIniciada(link);
    } else {
      ContentManager.addContent(
        <Alert
          message={intl.formatMessage({
            id: 'labelNaoFoiPossivelGerarLinkPesquisaClima',
          })}
        />
      );
    }
  };

  const handleConfirmeBeforeSave = async (
    model,
    salvar,
    intlConfirmacao,
    fechar,
    resourcesConfirmacao,
    modelInicial,
    validar
  ) => {
    if (verificandoAplicacoesFuturasRef.current) return;

    if (!validar()) {
      selectInvalidPesquisaTab(model);
      iniciarPesquisaAfterSaveRef.current = false;
      return;
    }

    if (!validateDataInicio(model, modelInicial)) {
      iniciarPesquisaAfterSaveRef.current = false;
      return;
    }

    if (!modelInicial?.id || !model.formularioId) {
      salvar(fechar);
      return;
    }

    verificandoAplicacoesFuturasRef.current = true;

    try {
      const response = await http.get('/FormularioGlobal/Edit', {
        params: { id: model.formularioId },
      });
      const formulario = response.data?.model || response.data || {};
      const hoje = format(new Date(), 'yyyy-MM-dd');
      const possuiAplicacoesFuturas = (formulario.usos || []).some(
        (uso) => normalizeDataRecorrencia(uso?.dataInicio) > hoje
      );

      if (!possuiAplicacoesFuturas) {
        salvar(fechar);
        return;
      }

      const pesquisaResponse = await http.post('/PesquisaClima/Edit', {
        id: model.id,
        parameters: rest.parameters,
      });
      const pesquisaSalva =
        pesquisaResponse.data?.model || pesquisaResponse.data || {};

      if (!recorrenciaAlterada(model, pesquisaSalva)) {
        salvar(fechar);
        return;
      }

      const mensagem = intlConfirmacao.formatMessage(
        { id: 'msgAlteracaoRecorrenciaCancelaAplicacoesFuturas' },
        { pesquisa: resourcesConfirmacao.pesquisa }
      );

      ContentManager.addContent(
        <CriticalConfirm
          ignoreResourceTitle
          textCapitalizeTitle={false}
          titleCapitalizeNone
          title={intlConfirmacao.formatMessage(
            { id: 'labelAlterarRecorrenciaPesquisa' },
            { pesquisa: resourcesConfirmacao.pesquisa }
          )}
          message={mensagem.endsWith('.') ? mensagem : `${mensagem}.`}
          labelConfirm={intlConfirmacao.formatMessage({
            id: 'labelConfirmarAlteracao',
          })}
          handleCancel={() => {
            iniciarPesquisaAfterSaveRef.current = false;
          }}
          handleConfirm={() => {
            confirmarAlteracaoRecorrenciaRef.current = true;
            salvar(fechar);
          }}
        />
      );
    } catch (err) {
      iniciarPesquisaAfterSaveRef.current = false;
      errorHandler(err);
    } finally {
      verificandoAplicacoesFuturasRef.current = false;
    }
  };

  const handleSave = async (model, handleClose, fechar) => {
    const shouldIniciarPesquisa = iniciarPesquisaAfterSaveRef.current;
    iniciarPesquisaAfterSaveRef.current = false;
    const confirmarAlteracaoRecorrencia =
      confirmarAlteracaoRecorrenciaRef.current;
    confirmarAlteracaoRecorrenciaRef.current = false;
    const aplicacaoRecorrente =
      getLookupId(model.aplicacao?.tipoAplicacaoId) ===
      TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE;

    if (aplicacaoRecorrente && !validateEnvio(model)) {
      return null;
    }

    const podeAlterarCampos = model.podeAlterar !== false;
    const formatoExibicao = normalizeFormatoExibicao(model.formatoExibicao);
    const exibirPerguntaPorVez =
      formatoExibicao === FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PERGUNTA_POR_VEZ;
    const modelToSave = {
      ...model,
      randomizarPerguntas: !!model.randomizarPerguntas,
      formatoExibicao,
      exibirBarraProgresso:
        exibirPerguntaPorVez && !!model.exibirBarraProgresso,
      confirmarAlteracaoRecorrencia,
    };

    if (
      podeAlterarCampos &&
      formComponentRef.current?.validateFormularioGlobal?.() === false
    ) {
      setActiveTabIndex(FORMULARIO_TAB_INDEX);
      return null;
    }

    const response = await http.post('/PesquisaClima/Save', {
      model: modelToSave,
      parameters: rest.parameters,
    });

    const savedModel = {
      ...modelToSave,
      ...response.data.model,
      confirmarAlteracaoRecorrencia: false,
    };

    if (podeAlterarCampos) {
      const savedFormulario =
        await formComponentRef.current?.saveFormularioGlobal(savedModel.id);

      if (savedFormulario?.id) {
        savedModel.formularioId = savedFormulario.id;
      }
    }

    await formComponentRef.current?.saveParticipantes(savedModel.id);

    if (shouldIniciarPesquisa) {
      await iniciarPesquisa(savedModel.id);
    }

    enqueueSnackbar(
      intl.formatMessage({
        id: 'label.registroSalvoComSucesso',
      }),
      { variant: 'success' }
    );

    if (fechar) {
      handleClose();
    }

    return savedModel;
  };

  const renderEnvioButtonActions = (
    model,
    isNew,
    formValid,
    showAllErrors,
    handleClose,
    handleSaveBeforeSave,
    isLoading
  ) => {
    if (!isEnvioTab || model.podeAlterar === false) {
      return null;
    }

    return (
      <Button
        className="ml-2"
        disabled={isLoading}
        onClick={async () => {
          if (!formValid) {
            selectInvalidPesquisaTab(model);
            showAllErrors();
            ContentManager.addContent(
              <Alert
                message={intl.formatMessage({
                  id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
                })}
              />
            );
            return;
          }

          if (!validateEnvio(model)) {
            return;
          }

          const resumo =
            (await formComponentRef.current?.getResumoIniciarPesquisa?.()) ||
            {};

          ContentManager.addContent(
            <ConfirmarInicioPesquisaModal
              nome={model.nome}
              dataInicio={model.dataInicio}
              dataTermino={getDataTerminoAplicacao(model)}
              participantesCount={resumo.participantesCount ?? 0}
              perguntasCount={resumo.perguntasCount ?? 0}
              tipoIdentificacaoId={getLookupId(model.tipoIdentificacao)}
              onConfirm={() => {
                iniciarPesquisaAfterSaveRef.current = true;
                handleSaveBeforeSave(true);
              }}
            />
          );
        }}
      >
        {intl.formatMessage({ id: 'labelIniciarPesquisa' })}
      </Button>
    );
  };

  return (
    <Edit
      url="/PesquisaClima"
      title={capitalizeFirstLetter(
        intl.formatMessage(
          { id: 'labelPesquisaClima' },
          { pesquisa: resources.pesquisa }
        )
      )}
      titleField="nome"
      ignoreResourceTitle={true}
      textCapitalizeTitle={false}
      titleCapitalizeNone={true}
      formComponent={FormEdit}
      formComponentRef={formComponentRef}
      activeTabIndex={activeTabIndex}
      aplicacaoRefreshToken={aplicacaoRefreshToken}
      actionsRefreshId={`${activeTabIndex}-${showEnvioRequiredErrors}`}
      onTabChange={(tabIndex) => {
        setActiveTabIndex(tabIndex);
        if (tabIndex !== ENVIO_TAB_INDEX) {
          setShowEnvioRequiredErrors(false);
        }
      }}
      linkPesquisa={linkPesquisa}
      onAplicacaoAlterada={(link) => {
        if (link) showPesquisaIniciada(link);
        setAplicacaoRefreshToken((prev) => prev + 1);
      }}
      showEnvioRequiredErrors={showEnvioRequiredErrors}
      handleSave={handleSave}
      handleConfirmeBeforeSave={handleConfirmeBeforeSave}
      renderRightButtonActions={renderEnvioButtonActions}
      schema={createPesquisaClimaValidation(intl)}
      showSaveClose
      colorCancelButton={isEnvioTab ? 'link' : undefined}
      classCancelButton={isEnvioTab ? 'px-0' : undefined}
      colorSaveButton={isEnvioTab ? 'outline-primary' : undefined}
      colorSaveCloseButton={isEnvioTab ? 'outline-primary' : undefined}
      width="85%"
      height={720}
      {...rest}
      refreshId={`${rest.refreshId ?? ''}-${aplicacaoRefreshToken}`}
    />
  );
}

export default PesquisaClimaEditView;
